import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as api from '@/features/backlog/api';
import { ActivityFeedItem } from '@/features/backlog/types';
import { stripMentions } from '@/features/backlog/mentions';
import { timeAgo } from '@/features/backlog/format';
import { taskPath, whiteboardPath } from '@/constants/routes';
import { useAppData } from '@/app-data';
import { useIdentity } from '@/features/identity/identity';
import { isDesktopApp } from '@/features/identity/desktop-updates';
import { Popover } from '@/components/ui/Popover';
import { Bell } from '@/components/ui/icons';
import MemberAvatar from '@/components/member-avatar';
import { MentionsPanel } from '@/components/comments-inbox';
import { MyCommentsPanel } from '@/components/my-comments-panel';
import { cn } from '@/components/ui/cn';

const SEEN_KEY = 'spark-board.bell-seen';
const FEED_DAYS = 7;

const ts = (s: string): number => {
  const t = Date.parse(s);
  return Number.isNaN(t) ? 0 : t;
};

/**
 * Where clicking a feed entry should deep-link + flash within the item: the exact comment, the
 * changed field, or the attachments section. Returned as a `?hl=` value the drawer resolves against
 * `[data-hl]` (see item-drawer). Null → just open the item.
 */
export function highlightTarget(a: ActivityFeedItem): string | null {
  if (a.kind === 'comment' || a.kind === 'mention') return a.ref_id ? `comment:${a.ref_id}` : 'comments';
  if (a.kind === 'attachment') return 'attachments';
  if (a.kind === 'created') return null;
  if (a.field === 'status' || a.kind === 'status') return 'field:status';
  if (a.field === 'priority' || a.kind === 'priority') return 'field:priority';
  if (a.field === 'assignee' || a.kind === 'assigned') return 'field:assignee';
  if (a.field === 'title') return 'field:title';
  return 'properties'; // a generic update — flash the whole properties card
}

/**
 * Header notification bell + flyout. Shows the caller's PERSONAL ACTIVITY FEED (`/me/activity`) for
 * the last {@link FEED_DAYS} days — every change on items they own / are assigned / PdM / watch, plus
 * comments that @mention them. Clicking an entry deep-links to the item AND scrolls to + flashes the
 * exact thing that changed (the comment, the field, attachments). This is the same relevant-to-me
 * stream the digest emails summarise, so the bell is no longer empty just because nobody @mentioned you.
 *
 * "Unread" is client-side: entries newer than the last time this member opened the bell (persisted in
 * localStorage). Opening marks everything seen — the current view still highlights what was new.
 */
export default function NotificationBell() {
  const { me } = useIdentity();
  const { members } = useAppData();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'activity' | 'mentions' | 'my-comments'>('activity');
  // Unresolved @mentions count — fed to the bell badge (kept fresh even while closed) and the tab
  // pill. The Mentions tab reports live updates via onCount when the flyout is open.
  const [mentionCount, setMentionCount] = useState(0);
  const [list, setList] = useState<ActivityFeedItem[]>([]);
  const [lastSeen, setLastSeen] = useState<string>(() => {
    try {
      return localStorage.getItem(SEEN_KEY) ?? '';
    } catch {
      return '';
    }
  });
  // The "seen" cutoff captured at the moment the flyout was opened, so its rows keep highlighting
  // what was new even though opening immediately advances `lastSeen` (clearing the badge).
  const viewSeen = useRef<string>(lastSeen);
  const bellToggle = useRef<(() => void) | null>(null); // captured Popover toggle, for the strip's bell
  const bellClose = useRef<(() => void) | null>(null); // captured Popover close, for tab-switch dismiss
  // The header bell's full click behaviour (toggle + mark-seen/load on open), captured fresh each render
  // so the desktop strip bell can invoke it with parity — see __sparkOpenBell below.
  const bellActivate = useRef<(() => void) | null>(null);
  const memberById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);

  const load = useCallback(() => {
    api.myActivityFeed(FEED_DAYS, 30).then(setList).catch(() => undefined);
  }, []);

  const loadMentions = useCallback(() => {
    api
      .listMyMentions(40)
      .then((ms) => setMentionCount(ms.filter((m) => !m.resolved).length))
      .catch(() => undefined);
  }, []);

  // Keep the feed + mention count (and thus the badge) fresh while signed in.
  useEffect(() => {
    if (!me) return undefined;
    load();
    loadMentions();
    const t = window.setInterval(() => {
      load();
      loadMentions();
    }, 60000);
    return () => window.clearInterval(t);
  }, [me, load, loadMentions]);

  const unread = useMemo(() => {
    const seen = ts(lastSeen);
    return list.filter((a) => ts(a.created_at) > seen).length;
  }, [list, lastSeen]);
  const badge = unread + mentionCount;

  // Desktop app: mirror the count onto the app icon (dock/taskbar) AND feed the account chip + bell in
  // the tab strip (name / initials / colour / unread). No-op in a browser.
  useEffect(() => {
    const d = (
      window as {
        __sparkDesktop?: {
          badge?: (n: number) => void;
          account?: (a: { name: string; initials: string; color: string; unread: number }) => void;
        };
      }
    ).__sparkDesktop;
    if (!d) return;
    d.badge?.(badge);
    if (me) {
      const parts = me.name.trim().split(/\s+/).filter(Boolean);
      const initials = (parts.length >= 2 ? parts[0][0] + parts[1][0] : me.name.slice(0, 2)).toUpperCase();
      d.account?.({ name: me.name, initials, color: me.color || '#71717a', unread: badge });
    }
  }, [badge, me]);

  // The strip's bell button opens THIS flyout via window.__sparkOpenBell; the shell closes it on tab
  // switch via window.__sparkCloseBell (both routed by the shell).
  useEffect(() => {
    const w = window as {
      __sparkOpenBell?: () => void;
      __sparkCloseBell?: () => void;
      __sparkCloseAccount?: () => void;
      __sparkMarkBellSeen?: () => void;
    };
    w.__sparkOpenBell = () => {
      w.__sparkCloseAccount?.(); // the two strip flyouts are mutually exclusive
      // Run the header bell's own click handler (not a bare toggle) so opening from the strip ALSO
      // marks the activity feed seen + reloads it — parity with the header bell. A bare toggle left the
      // badge stuck (only the tray's __sparkMarkBellSeen cleared it); this is the desktop bell bug fix.
      (bellActivate.current ?? bellToggle.current)?.();
    };
    w.__sparkCloseBell = () => bellClose.current?.();
    // The desktop menu-bar/tray popover shows the same feed; opening it marks activity read here too (so
    // the app-icon + strip badge clear, matching a bell open) WITHOUT opening this flyout. Advancing
    // lastSeen recomputes `unread` → the badge effect re-pushes the cleared count via the account beacon.
    w.__sparkMarkBellSeen = () => {
      const now = new Date().toISOString();
      setLastSeen(now);
      try {
        localStorage.setItem(SEEN_KEY, now);
      } catch {
        /* ignore unavailable storage */
      }
    };
    return () => {
      delete w.__sparkOpenBell;
      delete w.__sparkCloseBell;
      delete w.__sparkMarkBellSeen;
    };
  }, []);

  if (!me) return null;

  const markSeen = () => {
    const now = new Date().toISOString();
    setLastSeen(now);
    try {
      localStorage.setItem(SEEN_KEY, now);
    } catch {
      /* ignore unavailable storage */
    }
  };

  const openEntry = (a: ActivityFeedItem, close: () => void) => {
    if (a.whiteboard_id) {
      // Whiteboard @mention → open the board at the mentioned comment's thread.
      navigate(whiteboardPath(a.whiteboard_id) + (a.ref_id ? `?comment=${a.ref_id}` : ''));
      close();
      return;
    }
    const target = highlightTarget(a);
    navigate(taskPath(a.board_slug, a.item_key) + (target ? `?hl=${target}` : ''));
    close();
  };

  return (
    <Popover
      // Desktop: centre the flyout under the strip's bell button; right-align in the browser header.
      align={isDesktopApp() ? 'center' : 'right'}
      // Desktop: portal the flyout so it survives the strip's hidden (opacity-0) trigger wrapper.
      portal={isDesktopApp()}
      // Own the flyout's scroll: kill the panel's default padding + short max-height so there's a
      // SINGLE vertical scroll (no nested scrollers) and no horizontal scrollbar.
      panelClassName="w-[360px] !max-h-[70vh] !overflow-x-hidden !p-0"
      trigger={({ toggle, open: isOpen, close }) => {
        bellToggle.current = toggle;
        bellClose.current = close;
        const activate = () => {
          const opening = !isOpen;
          toggle();
          if (opening) {
            viewSeen.current = lastSeen; // freeze the highlight cutoff for this view
            load();
            markSeen();
          }
        };
        bellActivate.current = activate; // parity handler for the desktop strip bell (__sparkOpenBell)
        return (
        <button
          type="button"
          onClick={activate}
          aria-label={`Notifications${badge ? ` (${badge})` : ''}`}
          className={cn(
            'relative flex h-9 w-9 items-center justify-center rounded-full transition-colors',
            isOpen ? 'bg-layer text-fg-strong' : 'text-fg-muted hover:bg-layer-strong hover:text-fg',
          )}
        >
          <Bell />
          {badge > 0 ? (
            <span className="absolute -right-0.5 -top-0.5 min-w-[16px] rounded-full bg-brand px-1 text-center text-[10px] font-bold leading-4 text-ink">
              {badge > 9 ? '9+' : badge}
            </span>
          ) : null}
        </button>
        );
      }}
    >
      {(close) => (
        <>
          <div className="sticky top-0 z-10 flex items-center gap-1 border-b border-line bg-surface-raised px-1.5 py-1.5">
            <button
              type="button"
              onClick={() => setTab('activity')}
              className={cn(
                'flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[12.5px] font-semibold transition-colors',
                tab === 'activity' ? 'bg-layer text-fg-strong' : 'text-fg-muted hover:bg-layer/60 hover:text-fg',
              )}
            >
              Activity
              {unread > 0 ? <span className="h-1.5 w-1.5 rounded-full bg-accent" /> : null}
            </button>
            <button
              type="button"
              onClick={() => setTab('mentions')}
              className={cn(
                'flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[12.5px] font-semibold transition-colors',
                tab === 'mentions' ? 'bg-layer text-fg-strong' : 'text-fg-muted hover:bg-layer/60 hover:text-fg',
              )}
            >
              Mentions
              {mentionCount > 0 ? (
                <span className="min-w-[16px] rounded-full bg-accent px-1 text-center text-[10px] font-bold leading-4 text-white">
                  {mentionCount > 9 ? '9+' : mentionCount}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              onClick={() => setTab('my-comments')}
              className={cn(
                'flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[12.5px] font-semibold transition-colors',
                tab === 'my-comments' ? 'bg-layer text-fg-strong' : 'text-fg-muted hover:bg-layer/60 hover:text-fg',
              )}
            >
              My comments
            </button>
          </div>
          <div className={cn('p-1.5', tab === 'activity' ? '' : 'hidden')}>
            {list.length === 0 ? (
              <div className="px-3 py-10 text-center text-[12px] text-fg-faint">
                No activity on items you own, watch, or are assigned — yet.
              </div>
            ) : (
              list.map((a, i) => {
                const actor = a.actor_id ? memberById.get(a.actor_id) : undefined;
                const isNew = ts(a.created_at) > ts(viewSeen.current);
                return (
                  <button
                    key={`${a.item_id}-${a.created_at}-${i}`}
                    type="button"
                    onClick={() => openEntry(a, close)}
                    className={cn('flex w-full items-start gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-layer', isNew && 'bg-accent/5')}
                  >
                    <MemberAvatar member={actor} size={28} className="mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] leading-snug text-fg-soft">
                        <span className="font-semibold text-fg">{actor?.name ?? a.actor_name ?? 'Someone'}</span> {a.summary}
                        {a.detail ? <span className="font-semibold text-fg"> {a.detail}</span> : null}
                      </div>
                      {a.snippet ? <div className="mt-0.5 truncate text-[11.5px] text-fg-faint">{stripMentions(a.snippet)}</div> : null}
                      <div className="mt-1 flex items-center gap-x-1.5 text-[11px] text-fg-ghost">
                        <span className="shrink-0">{timeAgo(a.created_at)}</span>
                        {a.whiteboard_id ? (
                          <span className="min-w-0 flex-1 truncate text-fg-soft">🖊 {a.whiteboard_name || 'Whiteboard'}</span>
                        ) : (
                          <>
                            <span className="item-key shrink-0 whitespace-nowrap rounded px-1 py-px tnum text-[10px] font-semibold">{a.item_key}</span>
                            {a.item_title ? <span className="min-w-0 flex-1 truncate text-fg-soft">{a.item_title}</span> : null}
                          </>
                        )}
                        {a.relation === 'mention' ? (
                          <span className="shrink-0 whitespace-nowrap rounded bg-brand/15 px-1 py-px text-[10px] font-semibold text-brand">mention</span>
                        ) : null}
                      </div>
                    </div>
                    {isNew ? <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-accent" /> : null}
                  </button>
                );
              })
            )}
          </div>
          {/* Mentions stays mounted (hidden) so it keeps reporting its unresolved count to the badge. */}
          <div className={cn('p-1.5', tab === 'mentions' ? '' : 'hidden')}>
            <MentionsPanel close={close} onCount={setMentionCount} />
          </div>
          {/* My comments: no badge to feed, so mount lazily (only when active) to skip an extra fetch. */}
          {tab === 'my-comments' ? (
            <div className="p-1.5">
              <MyCommentsPanel close={close} />
            </div>
          ) : null}
        </>
      )}
    </Popover>
  );
}
